// rraRequestDraft — the RRA v2.1 requester-form state, shared by the public form
// (RraFormPage) and the console's staff fill-in (AccommodationsPage IntakePanel), so
// both ask for — and store — exactly the same thing in exactly the same words.
//
// Pure: no React, no fetch. Every consumer-facing word comes from rraCopy.ts (the
// byte-identical twin of the API's rraCopy); the only strings defined HERE are the
// handful of form-UI lines the consensus gives no wording for, marked DRAFT and listed
// in docs/design/rra-v2-1-copy/W1.md for Miles's approval.
//
//   cd apps/web && npx tsx --test src/lib/rraRequestDraft.test.ts
//
// THE FORM IS SHAPED BY THE GATE. `shape` (from the server, recomputed live) says which
// sections exist. The pure helpers below — missingItems, buildSubmissionBody — read it,
// so the browser asks for exactly what the server will accept and no more (the server
// drops anything outside the shape regardless).

import {
  AFFIRMATION_HEADING,
  CONFIRM_HEADING,
  LETTER_UPLOAD_LABEL,
  REQUEST_HEADING,
  RRA_HELP_MAX,
  RRA_REQUEST_TEXT_MAX,
  SIGN_LABEL,
  VERIFIER_NAME_LABEL,
  WHO_FOR,
  adultSubjectPermissionLine,
  attestationLine,
  needAffirmation,
  requestLabel,
  subjectEmailLabel,
  subjectSignLabel,
  type RraCopySubject,
} from './rraCopy';
import type {
  RraFormItemSubmission,
  RraFormItemView,
  RraFormShape,
  RraFormSubmissionBody,
  RraFormVerifier,
  RraFormView,
  RraRequestStatement,
} from './rraFormClient';

/** Free-text caps — the API enforces the same numbers (routes/rraForm.ts). */
export const REQUEST_TEXT_MAX = RRA_REQUEST_TEXT_MAX;
export const HELP_MAX = RRA_HELP_MAX;

/** Form-UI lines the consensus words do not cover (DRAFT — docs/design/rra-v2-1-copy/W1.md). */
export const FORM_UI = {
  /** The inline helper under a field that the "Still needed" list names. */
  fieldNeeded: 'This is still needed.', // DRAFT
  /** The eyebrow, the H1 fallback and the closed / unavailable pages predate v2.1 and are unchanged. */
  submitting: 'Submitting…', // DRAFT (the v2 button label while busy, kept)
} as const;

// ---------------------------------------------------------------------------
// One item's request draft
// ---------------------------------------------------------------------------

/** The editable state for ONE item's request + help box. */
export interface RequestDraft {
  /** The animal option (animal items only; pre-checked when nothing is on file). */
  allowAnimal: boolean;
  /** The checkbox; the input below it shows only while checked. */
  otherRuleOn: boolean;
  otherRule: string;
  physicalChangeOn: boolean;
  physicalChange: string;
  /** B4: the one optional "how this would help" box (<=300). */
  howChangeHelps: string;
}

export function blankRequestDraft(): RequestDraft {
  return {
    allowAnimal: false,
    otherRuleOn: false,
    otherRule: '',
    physicalChangeOn: false,
    physicalChange: '',
    howChangeHelps: '',
  };
}

/** The slice of an item view a draft is seeded from (public + staff views). */
export interface RequestDraftSource {
  scope: string;
  description?: string | undefined;
  request?: RraRequestStatement | undefined;
  howChangeHelps?: string | undefined;
}

function isAnimalScope(scope: string): boolean {
  return scope === 'esa_animal' || scope === 'service_animal';
}

/**
 * Seed a draft from what is already on file. When nothing is on file and `preselect` is
 * true (the requester's own form), an animal scope pre-checks the animal option ("we've
 * checked it for you — uncheck it if it doesn't fit", B3) and a `modification` item
 * pre-checks the physical change, prefilling its input from the item's neutral one-liner.
 * Staff fill-in passes `preselect: false` — staff record only what the requester said.
 */
export function requestDraftFromItem(
  item: RequestDraftSource,
  opts: { preselect: boolean },
): RequestDraft {
  const d = blankRequestDraft();
  d.howChangeHelps = item.howChangeHelps ?? '';
  if (item.request) {
    d.allowAnimal = isAnimalScope(item.scope) && item.request.allowAnimal;
    d.otherRule = item.request.otherRule ?? '';
    d.otherRuleOn = d.otherRule !== '';
    d.physicalChange = item.request.physicalChange ?? '';
    d.physicalChangeOn = d.physicalChange !== '';
    return d;
  }
  if (opts.preselect) {
    if (isAnimalScope(item.scope)) {
      d.allowAnimal = true;
    } else if (item.scope === 'modification') {
      d.physicalChangeOn = true;
      d.physicalChange = item.description ?? '';
    }
  }
  return d;
}

/** The wire shape: the animal option only counts on an animal item; the text options count only
 *  when their box is checked AND something is typed. Optional keys are added only when set. */
export function requestFromDraft(
  d: RequestDraft,
  isAnimal: boolean,
): { allowAnimal: boolean; otherRule?: string; physicalChange?: string } {
  const otherRule = d.otherRuleOn ? d.otherRule.trim() : '';
  const physicalChange = d.physicalChangeOn ? d.physicalChange.trim() : '';
  return {
    allowAnimal: isAnimal && d.allowAnimal,
    ...(otherRule ? { otherRule } : {}),
    ...(physicalChange ? { physicalChange } : {}),
  };
}

/** At least one option is set (a checked text option with nothing typed does not count — the same
 *  rule the API enforces). */
export function hasAnyRequest(d: RequestDraft, isAnimal: boolean): boolean {
  const r = requestFromDraft(d, isAnimal);
  return r.allowAnimal || r.otherRule !== undefined || r.physicalChange !== undefined;
}

/**
 * The staff fill-in body for one item: whatever the requester actually said, nothing more. Each
 * part is optional, so a partial phone call still records. The help box counts only where the form
 * would show it. Returns only the keys that are set (exactOptionalPropertyTypes-safe).
 */
export function staffItemFields(
  d: RequestDraft,
  opts: { isAnimal: boolean; helpShown: boolean },
): { request?: { allowAnimal: boolean; otherRule?: string; physicalChange?: string }; howChangeHelps?: string } {
  const help = d.howChangeHelps.trim();
  return {
    ...(hasAnyRequest(d, opts.isAnimal) ? { request: requestFromDraft(d, opts.isAnimal) } : {}),
    ...(opts.helpShown && help ? { howChangeHelps: help } : {}),
  };
}

// ---------------------------------------------------------------------------
// The whole requester form
// ---------------------------------------------------------------------------

export type AuthChoice = 'contacts' | 'letter' | 'later';
export type SubjectChoice = 'me' | 'someone' | '';

export interface VerifierDraft {
  name: string;
  role: string;
  email: string;
  phone: string;
}

export const EMPTY_VERIFIER: VerifierDraft = { name: '', role: '', email: '', phone: '' };

export interface ItemFormState {
  itemId: string;
  scope: RraFormItemView['scope'];
  isAnimal: boolean;
  /** Need is in the gap for this item. */
  inNeedGap: boolean;
  /** RRA v2.2: the server's position of this request among all the case's requests (see RraFormItemView). */
  ordinal?: number;
  draft: RequestDraft;
  /** The two permitted questions (a service-animal item with need in the gap only). */
  requiredBecauseOfDisability: boolean | undefined;
  supportProvided: string;
}

export interface FormState {
  shape: RraFormShape;
  items: ItemFormState[];
  subjectChoice: SubjectChoice;
  subjectName: string;
  /** undefined = not answered (no default). */
  subjectMinor: boolean | undefined;
  affirmDisability: boolean;
  /** The two possible need-affirmation lines (animal wording / other wording). */
  affirmNeedAnimal: boolean;
  affirmNeedOther: boolean;
  /** '' = not chosen (no default). */
  authChoice: AuthChoice | '';
  verifiers: VerifierDraft[];
  letterOnFile: boolean;
  /** An adult household member: send them a link, or they are here to sign. */
  subjectPermChoice: '' | 'link' | 'here';
  subjectEmail: string;
  subjectSignedName: string;
  signedName: string;
}

/** Seed one item's state from the view. */
export function itemFormStateFromView(item: RraFormItemView): ItemFormState {
  return {
    itemId: item.itemId,
    scope: item.scope,
    isAnimal: item.isAnimal,
    inNeedGap: item.inNeedGap,
    ...(item.ordinal !== undefined ? { ordinal: item.ordinal } : {}),
    draft: requestDraftFromItem(item, { preselect: true }),
    requiredBecauseOfDisability: item.serviceAnimalQuestions?.requiredBecauseOfDisability,
    supportProvided: item.serviceAnimalQuestions?.supportProvided ?? '',
  };
}

/** The initial form state from a fillable view. Nothing is pre-chosen except what is on file. */
export function initialFormState(view: RraFormView): FormState {
  const subject = view.subject;
  return {
    shape: view.shape,
    items: view.items.map(itemFormStateFromView),
    subjectChoice: !subject ? '' : subject.kind === 'self' ? 'me' : 'someone',
    subjectName: subject && subject.kind === 'household_member' ? subject.name : '',
    subjectMinor: subject && subject.kind === 'household_member' ? subject.minor : undefined,
    affirmDisability: false,
    affirmNeedAnimal: false,
    affirmNeedOther: false,
    authChoice: '',
    verifiers: [{ ...EMPTY_VERIFIER }],
    letterOnFile: view.uploadedLetters.length > 0,
    subjectPermChoice: '',
    subjectEmail: '',
    subjectSignedName: '',
    signedName: '',
  };
}

/** What the copy calls the household member until their name is typed (DRAFT). */
export const SUBJECT_NAME_FALLBACK = 'this person';

/** Whose voice the copy is in: the reader is the subject unless "Someone who lives with me". */
export function formVoice(state: Pick<FormState, 'subjectChoice' | 'subjectName' | 'subjectMinor'>): RraCopySubject {
  if (state.subjectChoice !== 'someone') return { readerIsSubject: true, name: '', minor: false };
  return {
    readerIsSubject: false,
    name: state.subjectName.trim() || SUBJECT_NAME_FALLBACK,
    minor: state.subjectMinor === true,
  };
}

/** An adult household member (not a minor): they sign their own permission (B2). */
export function isAdultMember(
  state: Pick<FormState, 'subjectChoice' | 'subjectName' | 'subjectMinor'>,
): boolean {
  return state.subjectChoice === 'someone' && state.subjectMinor === false;
}

/** The need-affirmation lines the shape shows: one per distinct wording, for non-service items with
 *  need in the gap (a service animal carries the two questions instead). */
export function needLines(state: Pick<FormState, 'items'>): { animal: boolean; other: boolean } {
  const eligible = state.items.filter((i) => i.inNeedGap && i.scope !== 'service_animal');
  return { animal: eligible.some((i) => i.isAnimal), other: eligible.some((i) => !i.isAnimal) };
}

/**
 * B6 (RRA v2.2): "What you're asking us to change", numbered "Request 1", "Request 2" when the form
 * carries several, by the server's `ordinal` (the request's place among ALL the case's requests) when it
 * sends one, else by the form's own order. The accommodation stage collects no animal details, so an animal's name is never
 * the label; the request is what they asked us to change.
 */
export function itemHeading(state: Pick<FormState, 'items'>, idx: number): string {
  const ordinal = state.items[idx]?.ordinal;
  // Numbered when the form carries several requests, or when it shows ONE that is not the case's first
  // (a form reopened for the newest request still says "Request 3"). A lone first request stays plain.
  if (state.items.length <= 1 && !(ordinal !== undefined && ordinal > 1)) return REQUEST_HEADING;
  return `${REQUEST_HEADING} — ${requestLabel(ordinal ?? idx + 1)}`;
}

/** DOM ids for the fields the "Still needed" list links to (ids are DOM anchors, never displayed). */
export const FIELD_ID = {
  who: 'rra-who',
  whoName: 'rra-who-name',
  whoMinor: 'rra-who-minor',
  request: (itemId: string): string => `rra-item-${itemId}-request`,
  disability: 'rra-disability',
  needAnimal: 'rra-need-animal',
  needOther: 'rra-need-other',
  confirm: 'rra-confirm',
  verifierName: (i: number): string => `rra-verifier-${i}-name`,
  letter: 'rra-letter',
  subjectPerm: 'rra-subject-permission',
  subjectEmail: 'rra-subject-email',
  subjectSign: 'rra-subject-sign',
  sign: 'rra-sign',
} as const;

export interface MissingItem {
  fieldId: string;
  label: string;
}

/**
 * B10: what is still needed, in PAGE ORDER, each named by the field's own label. The button is always
 * enabled; on press the page renders "Almost there. Still needed:" with a linked list of these, moves
 * focus to the list and marks each field. Empty = the form is complete (submit).
 */
export function missingItems(state: FormState): MissingItem[] {
  const out: MissingItem[] = [];
  const voice = formVoice(state);
  const subjectName = state.subjectName.trim();

  // B2 — who is this request for?
  if (state.subjectChoice === '') {
    out.push({ fieldId: FIELD_ID.who, label: WHO_FOR.heading });
  } else if (state.subjectChoice === 'someone') {
    if (!subjectName) out.push({ fieldId: FIELD_ID.whoName, label: WHO_FOR.nameLabel });
    if (state.subjectMinor === undefined) out.push({ fieldId: FIELD_ID.whoMinor, label: WHO_FOR.minorLabel });
  }

  // B3 — what each item asks us to change.
  state.items.forEach((item, idx) => {
    if (!hasAnyRequest(item.draft, item.isAnimal)) {
      out.push({ fieldId: FIELD_ID.request(item.itemId), label: itemHeading(state, idx) });
    }
  });

  // B5 — the disability attestation, once, only when disability is in the gap.
  if (state.shape.askDisability && !state.affirmDisability) {
    out.push({ fieldId: FIELD_ID.disability, label: attestationLine(voice) });
  }

  // B7 — the need affirmation(s), only when need is in the gap for a non-service item.
  const need = needLines(state);
  const shown: Array<{ id: string; checked: boolean; text: string }> = [];
  if (need.animal) shown.push({ id: FIELD_ID.needAnimal, checked: state.affirmNeedAnimal, text: needAffirmation(voice, true) });
  if (need.other) shown.push({ id: FIELD_ID.needOther, checked: state.affirmNeedOther, text: needAffirmation(voice, false) });
  for (const line of shown) {
    if (!line.checked) out.push({ fieldId: line.id, label: shown.length > 1 ? line.text : AFFIRMATION_HEADING });
  }

  // B8 — confirming the request, only when either prong is in the gap.
  if (state.shape.askConfirmation) {
    if (state.authChoice === '') {
      out.push({ fieldId: FIELD_ID.confirm, label: CONFIRM_HEADING });
    } else if (state.authChoice === 'contacts') {
      if (!state.verifiers.some((v) => v.name.trim())) {
        out.push({ fieldId: FIELD_ID.verifierName(0), label: VERIFIER_NAME_LABEL });
      }
      if (isAdultMember(state) && subjectName) {
        if (state.subjectPermChoice === '') {
          out.push({ fieldId: FIELD_ID.subjectPerm, label: adultSubjectPermissionLine(subjectName) });
        } else if (state.subjectPermChoice === 'link' && !state.subjectEmail.trim()) {
          out.push({ fieldId: FIELD_ID.subjectEmail, label: subjectEmailLabel(subjectName) });
        } else if (state.subjectPermChoice === 'here' && !state.subjectSignedName.trim()) {
          out.push({ fieldId: FIELD_ID.subjectSign, label: subjectSignLabel(subjectName) });
        }
      }
    } else if (state.authChoice === 'letter' && !state.letterOnFile) {
      out.push({ fieldId: FIELD_ID.letter, label: LETTER_UPLOAD_LABEL });
    }
  }

  // B10 — the typed signature (confirm-and-send signs nothing).
  if (!state.shape.confirmOnly && !state.signedName.trim()) {
    out.push({ fieldId: FIELD_ID.sign, label: SIGN_LABEL });
  }
  return out;
}

function verifierFromDraft(v: VerifierDraft): RraFormVerifier {
  return {
    name: v.name.trim(),
    ...(v.role.trim() ? { role: v.role.trim() } : {}),
    ...(v.email.trim() ? { email: v.email.trim() } : {}),
    ...(v.phone.trim() ? { phone: v.phone.trim() } : {}),
  };
}

/**
 * The POST body, exactly what the shape asked for: no help box outside the need gap or on a service
 * animal, the two questions on a service animal with need in the gap only, no attestation, signature
 * or contacts on confirm-and-send. (The server enforces the same rules and drops the rest.)
 */
export function buildSubmissionBody(state: FormState): RraFormSubmissionBody {
  const { shape } = state;
  const items: RraFormItemSubmission[] = state.items.map((item) => {
    const help = item.draft.howChangeHelps.trim();
    const isService = item.scope === 'service_animal';
    const support = item.supportProvided.trim();
    return {
      itemId: item.itemId,
      request: requestFromDraft(item.draft, item.isAnimal),
      ...(item.inNeedGap && !isService && help ? { howChangeHelps: help } : {}),
      ...(isService && item.inNeedGap && item.requiredBecauseOfDisability !== undefined
        ? { requiredBecauseOfDisability: item.requiredBecauseOfDisability }
        : {}),
      ...(isService && item.inNeedGap && support ? { supportProvided: support } : {}),
    };
  });

  const subject: RraFormSubmissionBody['subject'] =
    state.subjectChoice === 'someone'
      ? { kind: 'household_member', name: state.subjectName.trim(), minor: state.subjectMinor === true }
      : { kind: 'self' };

  if (shape.confirmOnly) {
    return { formVersion: 'v2.1', subject, items, affirmation: {}, confirmed: true };
  }

  const need = needLines(state);
  const needShown = need.animal || need.other;
  const authMethod = shape.askConfirmation && state.authChoice !== '' ? state.authChoice : undefined;
  const contacts = authMethod === 'contacts';
  const subjectPermission: RraFormSubmissionBody['subjectPermission'] | undefined =
    contacts && isAdultMember(state)
      ? state.subjectPermChoice === 'link'
        ? { via: 'link', email: state.subjectEmail.trim() }
        : state.subjectPermChoice === 'here'
          ? { via: 'typed', signedName: state.subjectSignedName.trim() }
          : undefined
      : undefined;

  return {
    formVersion: 'v2.1',
    subject,
    items,
    affirmation: {
      ...(shape.askDisability ? { hasDisability: true as const } : {}),
      ...(needShown ? { needsBecauseOfDisability: true as const } : {}),
    },
    ...(authMethod ? { authMethod } : {}),
    ...(contacts ? { verifiers: state.verifiers.filter((v) => v.name.trim()).map(verifierFromDraft) } : {}),
    ...(subjectPermission ? { subjectPermission } : {}),
    signedName: state.signedName.trim(),
  };
}

// ---------------------------------------------------------------------------
// The permission page (A6 / B2)
// ---------------------------------------------------------------------------

export interface PermissionPageState {
  choice: 'contacts' | 'letter' | '';
  verifiers: VerifierDraft[];
  signedName: string;
  /** A letter was sent in answer to THIS ask (uploaded after it was sent). */
  letterSent: boolean;
}

/** The permission page's "Still needed" list, in page order (the same B10 pattern as the form). */
export function missingPermissionItems(state: PermissionPageState): MissingItem[] {
  const out: MissingItem[] = [];
  if (state.choice === '') {
    out.push({ fieldId: FIELD_ID.confirm, label: CONFIRM_HEADING });
  } else if (state.choice === 'contacts') {
    if (!state.verifiers.some((v) => v.name.trim())) {
      out.push({ fieldId: FIELD_ID.verifierName(0), label: VERIFIER_NAME_LABEL });
    }
    if (!state.signedName.trim()) out.push({ fieldId: FIELD_ID.sign, label: SIGN_LABEL });
  } else if (!state.letterSent) {
    out.push({ fieldId: FIELD_ID.letter, label: LETTER_UPLOAD_LABEL });
  }
  return out;
}

/** The permission page's POST body. */
export function buildPermissionBody(
  state: PermissionPageState,
): { method: 'contacts'; verifiers: RraFormVerifier[]; signedName: string } | { method: 'letter' } {
  if (state.choice === 'letter') return { method: 'letter' };
  return {
    method: 'contacts',
    verifiers: state.verifiers.filter((v) => v.name.trim()).map(verifierFromDraft),
    signedName: state.signedName.trim(),
  };
}

/** Seed the permission page's verifier rows from the people the requester proposed on the form. */
export function verifierDraftsFromProposed(
  proposed: Array<{ name: string; role?: string; email?: string; phone?: string }>,
): VerifierDraft[] {
  if (proposed.length === 0) return [{ ...EMPTY_VERIFIER }];
  return proposed.slice(0, 3).map((v) => ({
    name: v.name,
    role: v.role ?? '',
    email: v.email ?? '',
    phone: v.phone ?? '',
  }));
}

